import { deleteObject, getDownloadURL, getMetadata, ref, updateMetadata } from 'firebase/storage';
import {
  assign, chain, cloneDeep, concat, forEach, get, includes, isEmpty, map, reduce,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import { uid } from 'quasar';
import { storage } from 'boot/firebase';
import ProgressReportElementsDB from 'src/fb/progressReportElements-db';
import ProgressReportsDB from 'src/fb/progressReports-db';
import ReportTemplateElementsDB from 'src/fb/reportTemplateElements-db';
import ReportTemplatesDB from 'src/fb/reportTemplates-db';
import GenericIndexedDb from 'src/indexed-db/generic-indexedDb';
import Interactions from 'src/mixins/Interactions';
import { firebaseUploader } from 'src/store/firebaseUploader';
import { network } from 'src/store/network';
import AppUtils from 'src/utils/AppUtils';
import { findBase64Images } from 'src/utils/HtmlUtils';
import { logEvent } from 'src/utils/LogEvent';
import { createSrcReplacer } from 'src/utils/OfflineAccessUtils';

/**
 * @typedef {
 *  'reportTemplates' | 'progressReports' | 'sessionActivities' | 'sessionNoteTemplates'
 * } StorageImageCollection
 */

/** Max size of custom metadata to be set without getting CORS and "Max retry limit" errors.  */
const MAX_CUSTOM_METADATA_SIZE = 2000;
/** The size of custom metadata when we stop paying attention to where it is located and keep the file indefinitely */
const CUSTOM_METADATA_SIZE_TO_PERSIST_INDEFINITELY = 100;
/** To be used as custom metadata key if we want to keep the file indefinitely */
const PERSIST_FILE_INDEFINITELY_KEY = 'PERSIST_INDEFINITELY';

export default {
  data() {
    return {
      imagesForDelete: [],
      imagesToKeep: [],
    };
  },

  mixins: [Interactions],

  computed: {
    ...mapState(network, ['offlineMode']),
  },

  methods: {
    ...mapActions(firebaseUploader, ['addToUploadQueue']),

    /**
     * Upload report elements' images to Firestore and replace it's textContent src with store link
     * @param {array} reportElements - array of elements that should be searched for images
     * @param {StorageImageCollection} collectionName - name of the collection
     * we use for storage images
     * @param {string} itemId - id that will be used in image storage path
     *
     * @return {Promise<{reportElementCopy: Object, index: number}[]>}
     */
    async uploadReportImages(reportElements, collectionName, itemId) {
      const data = { collectionName, itemId };

      // Upload report elements' images to firestore and update src
      const reportElementsActions = map(reportElements, async (reportElement, index) => {
        const reportElementCopy = cloneDeep(reportElement);
        const { contentType, textContent, chartHeaderText, chartFooterText } = reportElementCopy;

        if (['text', 'pageHeader', 'pageFooter'].includes(contentType) && textContent) {
          reportElementCopy.textContent = await this.replaceImageSources(textContent, data);
          return { reportElementCopy, index };
        }

        if (contentType === 'programGroup') {
          if (chartHeaderText || chartFooterText) {
            reportElementCopy.chartHeaderText = chartHeaderText
              ? await this.replaceImageSources(chartHeaderText, data) : '';
            reportElementCopy.chartFooterText = chartFooterText
              ? await this.replaceImageSources(chartFooterText, data) : '';
          }

          if (isEmpty(reportElementCopy.programContent)) {
            return { reportElementCopy, index };
          }

          // Checking if content is inside programContent field
          const programContentActions = map(reportElementCopy.programContent, async (value, key) => {
            const itemCopy = cloneDeep(value);

            if (itemCopy.chartHeaderText) {
              itemCopy.chartHeaderText = await this.replaceImageSources(itemCopy.chartHeaderText, data);
            }
            if (itemCopy.chartFooterText) {
              itemCopy.chartFooterText = await this.replaceImageSources(itemCopy.chartFooterText, data);
            }

            return { [key]: itemCopy };
          });

          let newProgramContent = await Promise.all(programContentActions);
          newProgramContent = reduce(newProgramContent, (acc, obj) => assign(acc, obj), {});

          reportElementCopy.programContent = newProgramContent;

          return { reportElementCopy, index };
        }
      });

      const uploadResults = await AppUtils.allSettled(reportElementsActions);
      return chain(uploadResults)
        .map((result) => (result.status === 'fulfilled' ? result.value : null))
        .compact()
        .value();
    },

    /**
     * @param {string} str
     * @param {{ collectionName: StorageImageCollection, itemId: string }} data
     */
    async replaceImageSources(str, data) {
      /*
        todo: can be replaced with `findBase64ImageDOMElements` method returning DOM elements array.
          That would be easier to maintain and give us more flexibility, but for now we should left
          `findBase64` using regex as we'll have to retest huge part of our system.
      */
      const base64Images = findBase64Images(str);

      // If there is no image in the string return it without changes
      if (base64Images.length === 0) {
        return str;
      }

      // Upload images to Firestore
      const imageActions = map(base64Images, async (imgSrc, index) => {
        const url = await this.persistBase64Image(imgSrc, index, data);

        return {
          match: imgSrc,
          newUrl: url,
        };
      });
      const updateImagesResults = await AppUtils.allSettled(imageActions, (err) => (
        this.notifyNegative(get(err, 'message', err))
      ));

      const updatedImages = chain(updateImagesResults).map((result) => result.value).compact().value();

      // Update string with new image sources
      let newStr = str;
      forEach(updatedImages, (obj) => {
        newStr = newStr.replaceAll(obj.match, obj.newUrl);
      });

      return newStr;
    },

    /**
     * Load base64 image into firebase storage.
     * @private
     * Used in {@link replaceImageSources}
     *
     * @param {string} imgSrc
     * @param {number} order
     * @param {{collectionName: StorageImageCollection, itemId: string}} data
     *
     * @return {Promise<string>} url of the saved image or ref id of the saved `filesUploading` document (offline mode)
     */
    async persistBase64Image(imgSrc, order, data) {
      const { collectionName, itemId } = data;
      const destinationPath = `${collectionName}/${itemId || uid()}/`;
      const destinationFilename = `${collectionName}-${uid()}-${order}.png`;
      let url;

      const uploadInfo = {
        destinationPath,
        destinationFilename,
        isBase64: true,
        file: imgSrc,
        metadata: {
          contentType: 'image/png',
          customMetadata: {
            [itemId]: collectionName,
          },
        },
        callback: async (imageRef) => {
          if (this.offlineMode) {
            url = createSrcReplacer(imageRef.id);
            return;
          }

          url = await getDownloadURL(imageRef);
        },
      };

      if (this.offlineMode) {
        uploadInfo.offline = true;
      }

      await this.addToUploadQueue(uploadInfo);

      return url;
    },

    // INTERACTIONS WITH METADATA METHODS

    /**
     * Set image file new metadata. If image file's metadata lefts empty - add to delete queue array.
     *
     * @param {string} filePath - Storage path of the image
     * @param {{ [id: string]: StorageImageCollection | null }} metadata - New metadata. If metadata key === null
     *  - clears field
     */
    async setImageFileMetadata(filePath, metadata) {
      if (this.offlineMode) {
        let metadataToSave = metadata;
        const filesUpdatingDb = new GenericIndexedDb('filesUpdating');
        const existingRecord = await filesUpdatingDb.read(filePath);

        if (existingRecord && existingRecord.metadata) {
          metadataToSave = {
            ...existingRecord.metadata,
            ...metadata,
          };
        }

        Object.keys(metadataToSave).forEach((path) => {
          if (metadataToSave[path] === null) {
            delete metadataToSave[path];
          }
        });

        if (!Object.keys(metadataToSave).length) {
          if (existingRecord) {
            return filesUpdatingDb.delete(filePath, true);
          }

          return;
        }

        return filesUpdatingDb.set({
          filePath,
          metadata: metadataToSave,
        }, filePath, true);
      }

      const fileRef = ref(storage, filePath);
      const currentMetadata = await getMetadata(fileRef);

      let { customMetadata } = currentMetadata;
      const originalCustomMetadataKeys = Object.keys(customMetadata);
      const originalCustomMetadataSize = originalCustomMetadataKeys.length;

      if (customMetadata[PERSIST_FILE_INDEFINITELY_KEY]) {
        // If file is set to be persisted indefinitely - we should just keep it
        this.imagesToKeep.push(filePath);
        return;
      }

      if (originalCustomMetadataSize >= CUSTOM_METADATA_SIZE_TO_PERSIST_INDEFINITELY) {
        if (originalCustomMetadataSize > MAX_CUSTOM_METADATA_SIZE) {
          // If custom metadata size is too big - we should cut off the excess to avoid patching errors
          customMetadata = originalCustomMetadataKeys
            .slice(0, (MAX_CUSTOM_METADATA_SIZE - 1))
            .reduce((acc, key) => {
              acc[key] = currentMetadata.customMetadata[key];
              return acc;
            }, {});
        }

        customMetadata[PERSIST_FILE_INDEFINITELY_KEY] = 'true';
      }

      const newMetadata = {
        customMetadata: {
          ...customMetadata,
          ...(customMetadata[PERSIST_FILE_INDEFINITELY_KEY] ? {} : metadata),
        },
      };
      const isMetadataEmpty = chain(newMetadata.customMetadata)
        .values()
        .compact()
        .isEmpty()
        .value();

      if (isMetadataEmpty && !customMetadata[PERSIST_FILE_INDEFINITELY_KEY]) {
        this.imagesForDelete.push(filePath);
      } else {
        this.imagesToKeep.push(filePath);
      }

      return updateMetadata(fileRef, newMetadata);
    }, // setImageFileMetadata()

    /**
     * Delete storage images from delete queue and reset it
     */
    async deleteUnusedImagesAndClearDeleteQueue() {
      if (isEmpty(this.imagesForDelete)) {
        this.imagesToKeep = [];
        return;
      }

      const deleteImagesActions = chain(this.imagesForDelete)
        .uniq()
        .map(async (filePath) => {
          if (!includes(this.imagesToKeep, filePath)) {
            return this.deleteImageFile(filePath);
          }
        })
        .value();

      await AppUtils.allSettled(deleteImagesActions);

      this.imagesForDelete = [];
      this.imagesToKeep = [];
    },

    /**
     * Deletes image file from storage if its custom metadata is empty;
     *
     * @private
     * Used in {@link deleteUnusedImagesAndClearDeleteQueue}
     *
     * @param {string} filePath - storage path of the image
     */
    async deleteImageFile(filePath) {
      const fileRef = ref(storage, filePath);
      const currentMetadata = await getMetadata(fileRef);

      if (isEmpty(currentMetadata.customMetadata)) {
        await deleteObject(fileRef);
      }
    },

    /**
     * Finds all images links in {@link str} and returns storage images paths from them
     *
     * @private
     * Used in {@link findElementImagesPaths}
     *
     * @param {string} str - string we parse for storage images links
     * @returns {string[]} - array of paths
     */
    findStorageImagesPaths(str) {
      // eslint-disable-next-line no-useless-escape
      const imageSrcPattern = /<img.+?src="https[\w\d:\/.-]+\/o\/([\w\d:\/.\-%]+).+?>/gi;
      const matches = new Set();

      // Find all external images path
      let match = imageSrcPattern.exec(str);
      while (match) {
        if (match && match[0] && includes(match[0], 'https://firebasestorage.googleapis.com/') && match[1]) {
          matches.add(decodeURIComponent(match[1]));
        }

        match = imageSrcPattern.exec(str);
      }

      return Array.from(matches);
    },

    /**
     * Find all images paths in {@link reportElement}
     *
     * @param {object} reportElement element of the reportTemplate or progressReport
     * @returns {string[]}
     */
    findElementImagesPaths(reportElement) {
      const { contentType, textContent, chartHeaderText, chartFooterText } = reportElement;
      const paths = [];

      if (['text', 'pageHeader', 'pageFooter'].includes(contentType) && textContent) {
        paths.push(...this.findStorageImagesPaths(textContent));
        return paths;
      }

      if (contentType === 'programGroup') {
        if (chartHeaderText) {
          paths.push(...this.findStorageImagesPaths(chartHeaderText));
        }
        if (chartFooterText) {
          paths.push(...this.findStorageImagesPaths(chartFooterText));
        }

        // Checking if content is inside programContent field
        if (isEmpty(reportElement.programContent)) {
          return paths;
        }

        forEach(reportElement.programContent, async (item) => {
          if (item.chartHeaderText) {
            paths.push(...this.findStorageImagesPaths(item.chartHeaderText));
          }
          if (item.chartFooterText) {
            paths.push(...this.findStorageImagesPaths(item.chartFooterText));
          }
        });
      }

      return paths;
    },

    /**
     * Updates metadata of all images in report element
     *
     * @param {object} reportElement element of the reportTemplate or progressReport
     * @param {object} data metadata
     */
    async updateElementImagesMetadata(reportElement, data) {
      const imagesPaths = this.findElementImagesPaths(reportElement);
      const updateImagesActions = map(imagesPaths, (path) => this.setImageFileMetadata(path, data));
      await AppUtils.allSettled(updateImagesActions);
    },

    /**
     * Updates metadata of all images in all report elements of the report template or progress report
     *
     * @param {object[]} reportElements
     * @param {'reportTemplates' | 'progressReports' | null} collectionName -
     * name of the document collection where images connected to.
     * If {@link collectionName} === null - clears metadata for {@link itemId};
     * @param {string} itemId - id of the document images connected to.
     */
    async updateReportImagesMetadata(reportElements, collectionName, itemId) {
      const data = {
        [itemId]: collectionName,
      };

      // Update all storage images' metadata in each report element
      const reportElementsActions = map(reportElements, async (reportElement) => {
        return this.updateElementImagesMetadata(reportElement, data);
      });

      try {
        await Promise.all(reportElementsActions);
      } catch (err) {
        console.error(err);
        logEvent(err);
      } finally {
        await this.deleteUnusedImagesAndClearDeleteQueue();
      }
    },

    /**
     * Clear related to template metadata of all images within report template
     *
     * @param {string} reportTemplateId
     */
    async deleteReportTemplateImagesMetadata(reportTemplateId) {
      const reportTemplatesDb = new ReportTemplatesDB();
      const reportTemplateElementsDb = new ReportTemplateElementsDB(reportTemplateId);

      const reportTemplate = await reportTemplatesDb.read(reportTemplateId);
      const reportTemplateElements = await reportTemplateElementsDb.readAll();
      const templateElementsWithLayout = concat(
        reportTemplateElements,
        this.getReportHeaderAndFooterAsElements(reportTemplate),
      );

      return this.updateReportImagesMetadata(
        templateElementsWithLayout,
        null,
        reportTemplateId,
      );
    }, // deleteProgressReportImagesMetadata()

    async deleteProgressReportImagesMetadata(progressReportId) {
      const progressReportsDb = new ProgressReportsDB();
      const progressReportElementsDb = new ProgressReportElementsDB(progressReportId);

      const progressReport = await progressReportsDb.read(progressReportId);
      const progressReportElements = await progressReportElementsDb.readAll();
      const progressReportElementsWithLayout = concat(
        progressReportElements,
        this.getReportHeaderAndFooterAsElements(progressReport),
      );

      return this.updateReportImagesMetadata(
        progressReportElementsWithLayout,
        null,
        progressReportId,
      );
    }, // deleteProgressReportImagesMetadata()

    /**
     * @param {Object} report
     * @return {{textContent, contentType: string}[]}
     */
    getReportHeaderAndFooterAsElements(report) {
      const elements = [];

      if (get(report, 'pageHeader')) {
        elements.push({
          contentType: 'pageHeader',
          textContent: report.pageHeader,
        });
      }

      if (get(report, 'pageFooter')) {
        elements.push({
          contentType: 'pageFooter',
          textContent: report.pageFooter,
        });
      }

      return elements;
    }, // getReportPageFooterAsElements()

  },
};
